<script setup>
import { reactive, ref } from "vue";
import { trackEvent } from "../utils/analytics";
import IconArrow from "./IconArrow.vue";

const props = defineProps({
  locale: { type: String, required: true },
  copy: { type: Object, required: true },
});

const state = ref("idle");
const form = reactive({
  name: "",
  email: "",
  phone: "",
  service: "",
  message: "",
  consent: false,
});

const contactApiUrl =
  process.env.VUE_APP_CONTACT_API_URL || "https://api.corpresi.com/api/form/contact";

async function submit() {
  state.value = "sending";
  try {
    const response = await fetch(contactApiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_name: form.name.trim(),
        user_email: form.email.trim(),
        user_tel: form.phone.trim(),
        user_project: `${form.service}\n\n${form.message}`.trim(),
      }),
    });

    if (!response.ok) throw new Error(`Contact API returned ${response.status}`);
    state.value = "success";
    trackEvent("generate_lead", { form: "contact", language: props.locale });
  } catch {
    state.value = "error";
  }
}

function errorText() {
  if (props.locale === "en") return "We could not send your enquiry. Please try again or contact us by email or WhatsApp.";
  if (props.locale === "it") return "Non è stato possibile inviare la richiesta. Riprova o contattaci via email o WhatsApp.";
  return "No pudimos enviar la consulta. Intentá nuevamente o escribinos por email o WhatsApp.";
}

function successText() {
  if (props.locale === "en") return "You can also reach us by email or WhatsApp.";
  if (props.locale === "it") return "Puoi anche contattarci via email o WhatsApp.";
  return "También podés escribirnos por email o WhatsApp.";
}
</script>

<template>
  <div v-if="state === 'success'" class="form-success" role="status">
    <span>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="m5 12 4 4L19 6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </span>
    <h3>{{ copy.success }}</h3>
    <p>{{ successText() }}</p>
  </div>

  <form v-else class="contact-form" @submit.prevent="submit">
    <div class="field-grid">
      <label class="field">
        <span>{{ copy.name }}</span>
        <input v-model="form.name" name="name" autocomplete="name" required>
      </label>
      <label class="field">
        <span>{{ copy.email }}</span>
        <input v-model="form.email" name="email" type="email" autocomplete="email" required>
      </label>
      <label class="field">
        <span>{{ copy.phone }}</span>
        <input v-model="form.phone" name="phone" type="tel" autocomplete="tel">
      </label>
      <label class="field">
        <span>{{ copy.service }}</span>
        <select v-model="form.service" name="service" required>
          <option value="" disabled>—</option>
          <option v-for="option in copy.options" :key="option" :value="option">{{ option }}</option>
        </select>
      </label>
    </div>
    <label class="field">
      <span>{{ copy.message }}</span>
      <textarea v-model="form.message" name="message" rows="5" required />
    </label>
    <label class="check-field">
      <input v-model="form.consent" type="checkbox" name="consent" required>
      <span>{{ copy.consent }}</span>
    </label>
    <p v-if="state === 'error'" class="form-error" role="alert">{{ errorText() }}</p>
    <button class="button button--primary form-submit" type="submit" :disabled="state === 'sending'">
      {{ state === "sending" ? copy.sending : copy.submit }}
      <IconArrow />
    </button>
  </form>
</template>
